'use client'
import React, { useState, useRef, Profiler } from 'react';
import dynamic from "next/dynamic";
import useMount from "react-use/lib/useMount";
import TEXT from "localisation";
import queryString from '@elastic/search-ui/lib/esm/queryString.js';
import Link from "next/link";
import { useNextRouting } from '../../../utils/useNextRouting';
import URLManager from '@elastic/search-ui/lib/cjs/URLManager.js';
import {
  Results,
  WithSearch,
} from "@elastic/react-search-ui";
import { useSearchParams, usePathname } from "next/navigation";


import styles from './search.module.css';

// Utils
import { getQs, getConfig } from './utils';
import logPageView from "lib/ga/pageView";
import logSearchPageView from "lib/ga/searchPageView";
import Connector from "../../../services/APIConnector";
import OurSearchProvider from './Results/OurSearchProvider';
import { processResultForCard, cardMapCleanup } from './processor';


// Context
import SearchContext from "./searchContext";

// Components
import { Loading } from "components/Loading";
import Bid from './Bids';
import Toolbar from './Toolbar';
import SearchData from './SearchData';

const SimpleMap = dynamic(() => import("./SimpleMap"), {
  ssr: false,
});

import { PaginationBar } from './PaginationBar';
import NoResults from "./NoResults";
import Pagination from "./Pagination";
import DisplayCard from "./Result/Card/DisplayCard";
import FilterPills from "./FilterPills"

// Types
import type { PropertyType, FilterType,  SearchComponentType } from "lib/types";
import type { SearchPageView } from 'lib/ga/_types'
import SponsoredBanner from '../Result/Sponsored/SponsoredBanner';


interface PropSchemePairing {
  [key: number]: string;
}

interface SearchResults {
  [key: number]: PropertyType;
}

type PageProps = {
  allResults: number[];
  searchCategory: string;
  concatenatedResults: PropSchemePairing;
  concatenations: PropSchemePairing;
  customStrapline?: boolean;
  customHead?: boolean;
  criteria: string;
  data: SearchResults;
  filters: any;
  info: any;
  initialFilters: any;
  location: any;
  name: string;
  portfolio: any;
  searchType: "PROPERTY" | "SCHEME";
  sponsoredOnly?: boolean;
  googleSearch?: boolean;
  // The server side query string
  searchParams: any;
  initialData?: any;

};




const MapCards = ({ results, filters, portfolio, searchType, sizeMeasurementType }) => {
  const hoverMethod = (id, hoverIn) => {
    let elem = document.getElementById(`marker-${id}`);
    if (elem) {
      if (hoverIn) {
        elem.classList.add("focussed");
      } else {
        elem.classList.remove("focussed");
      }
    }
  }
  if (!results) return null;
  return (
    <div className={`${styles.mapCards} ${styles.mapCardContainer}`}>
      {results.filter((r) => r.docType === 'scheme' || r.docType === 'property').map((r) => ({ result: r }))
        .map((r) => {
          return processResultForCard(r, filters, portfolio, searchType, sizeMeasurementType);
        })
        .map(cardMapCleanup)
        .map((r, i) => {
          const resultLinkRef = useRef(null);
          return (
            <DisplayCard displayExpandButton={false} key={r.id} onMouseEnter={() => hoverMethod(r.id, true)} mapView={true}
              onMouseLeave={() => hoverMethod(r.id, false)} {...r} selected={false} hideSearchScore resultLinkRef={resultLinkRef} sizeMeasurementType={sizeMeasurementType} />
          )
        })}
    </div>
  );
}

const CustomResultInner = ({ selected, updateSelected, totalOtherLocations, filters, props, portfolio, searchType, sizeMeasurementType, forSale, bids, dxStats, location }) => {
  const data = processResultForCard(props, filters, portfolio, searchType, sizeMeasurementType);
  const resultLinkRef = useRef(null);
  const noLazy = props.result.rn === 0;
  const scrollTo = () => {
    const element = document.getElementById('footer');
    element?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <>

      {(data.docType === 'scheme' || data.docType === 'property') && (
        <DisplayCard {...data} renderChildren={selected} properties={data.available_properties || []} displayExpandButton={true} selected={selected}
          onExpand={updateSelected} resultLinkRef={resultLinkRef} noLazy={noLazy} sizeMeasurementType={sizeMeasurementType} forSale={forSale} />)
      }

      {data.docType === 'bid' && bids && (
        <Bid bids={bids} />
      )}

      {data.docType === 'locations-advert' && totalOtherLocations > 0 && (
        <li className={styles.lookingForBox}>
          <div>
            <h2>Looking for nearby locations?</h2>
            <p>We have available shops in {totalOtherLocations} more locations near {location}.</p>
          </div>
          <button className={styles.secondaryButton} onClick={(e) => {
            scrollTo()
          }}>Find nearby locations</button>
        </li>
      )}

      {data.docType === 'dx-advert' && dxStats.collected && dxStats.collected > 0 && (
        <li className={styles.lookingForBox}>
          <div>
            <h2>We have researched {dxStats.collected} more properties matching your search</h2>
            <p>Upgrade to Data Exchange to see all {dxStats.collected + dxStats.property} available properties in {location}.</p>
          </div>
          <Link href={`/data-exchange`} passHref prefetch={false} >
            <button className={styles.secondaryButton}>Upgrade now</button>
          </Link>
        </li>
      )}
    </>
  )
}

const CustomResultView = (props) => {
  return (
    <SearchContext.Consumer>
      {({
        filters, portfolio, searchType,
        selected, updateSelected,
        sizeMeasurementType, forSale, bids, dxStats, totalOtherLocations, location }) => (
        <CustomResultInner
          bids={bids} dxStats={dxStats} location={location} totalOtherLocations={totalOtherLocations}
          selected={selected} updateSelected={updateSelected}
          filters={filters} portfolio={portfolio}
          props={props}
          searchType={searchType}
          sizeMeasurementType={sizeMeasurementType}
          forSale={forSale} />
      )}

    </SearchContext.Consumer>
  );
};

const ListView = ({ news, totalOtherLocations, location, bids, dxStats, currentPage, setCurrentPage, locationDetails, statistics, recommendations, destination, searchType, categoryCode, loading, filters, portfolio, searchCriteria, results, hideStatistics = false, sizeMeasurementType, forSale, ...props }) => {

  const [selected, updateSelected] = useState(null);
  const hasResults = Boolean(results.filter((r) => r.docType === 'scheme' || r.docType === 'property').length);
  return (
    <>
      {!hasResults && !loading && (
        <NoResults removeFilter={props.removeFilter} filters={filters} {...props} classType={styles.resultMap} />
      )}
      {hasResults && (
        <>
          <SearchContext.Provider value={{ filters, portfolio, searchType, selected, updateSelected, location, sizeMeasurementType, forSale, bids, dxStats, totalOtherLocations }}>
            <div className={styles.Resultscontainer}>
              {!loading && (
                <Results resultView={CustomResultView} urlField="url" thumbnailField="thumbnail_url" />
              )}
            </div>

          </SearchContext.Provider>
          <div className={styles.resultsFooter} id="footer">
            <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </div>
        </>

      )}
    </>
  )
}

const Map = ({ lat, lng, results, filters, portfolio, sizeMeasurementType, searchType, locationDetails }) => {
  let customResult = results.filter((r) => r.docType === 'scheme' || r.docType === 'property').map((r) => ({ result: r })).map((r) => {
    return processResultForCard(r, filters, portfolio, searchType, sizeMeasurementType);
  })
  return (
    <div className={styles.resultMap}>
      <SimpleMap lat={Number(lat)} lng={Number(lng)} results={customResult} locationDetails={locationDetails} />
    </div>

  )
}



const Search = ({ initialData, location, locationDetails, portfolio = null,
  googleSearch = false, lat, lng, categoryCode, searchPortfolio,
  searchCriteria, searchType, statistics, recommendations, destination = null,
  searchParams,
  baseSettings, news = null, bids = null, hideHeader = false, hideStatistics = false,
  hideLocationSearch = false, forSale = false, meta, dxStats }: SearchComponentType) => {
  const criteria = 'PROPERTY';
  const connector = new Connector(baseSettings);
  const includeDefaultAv = searchCriteria !== "COMPANY" && searchCriteria !== "PORTFOLIO" && searchType !== 'SCHEME';

  let asPath = usePathname();
  let query = useSearchParams();

  const initialDataWithFilters = { ...initialData };
  const queryParams = queryString.parse(getQs(asPath));

  if (queryParams?.filters && queryParams.filters && Array.isArray(queryParams.filters) && queryParams.filters.length) {
    initialDataWithFilters.filters = queryParams.filters;
  }

  const queryStringText = query?.toString();
  const urlManager = new URLManager();
  const initialState = urlManager.urlToState(queryStringText ?? '');
  initialDataWithFilters.filters = []

  const defaultFilters: FilterType[] = [];

  if (categoryCode === 'ALL') {
    const values: string[] = [];
    initialDataWithFilters?.facets?.category_code?.[0]?.data.forEach((e) => {
      values.push(e.value)
    })
    defaultFilters.push({ 'field': 'category_code', values: values, type: 'all' })
  } else {
    defaultFilters.push({ 'field': 'category_code', values: categoryCode, type: 'all' })
  }

  if (includeDefaultAv) {
    defaultFilters.push({ 'field': 'availability', values: forSale ? ['For Sale'] : ['To Let', 'Under Offer'], type: 'all' })
  }

  let includesAvailabilty = false;
  let includesCategory = false;
  let uiCategory = false;
  let uiavailability = false;
  if (initialState.filters) {
    initialState.filters.forEach((value, key) => {
      initialDataWithFilters.filters.push(value)
      if (value.field === 'availability') { includesAvailabilty = true };
      if (value.field === 'category_code') { includesCategory = true };
      if (value.field === 'ui:category_code') { uiCategory = true };
      if (value.field === 'ui:availability') { uiCategory = true };
    })
  }


  if (!includesAvailabilty && includeDefaultAv && !uiavailability) {
    initialDataWithFilters.filters.push({ 'field': 'availability', values: forSale ? ['For Sale'] : ['To Let', 'Under Offer'], type: 'all' })
  }

  if (!includesCategory && !uiCategory) {
    const values: string[] = []
    if (categoryCode === 'ALL') {
      initialDataWithFilters?.facets?.category_code?.[0]?.data.forEach((e) => {
        values.push(e.value)
      })
    } else {
      values.push(categoryCode)
    }
    initialDataWithFilters.filters.push({ 'field': 'category_code', values: values, type: 'all' })
  }

  const defaultSortOrder = queryParams.sort ? queryParams.sort : null;

  const config = getConfig(connector, []);

  const [showMap, setShowMap] = useState(query?.get('map'));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(Boolean(query?.get('showAdvancedFilters')));
  let noIndexNoFollow = false;
  if (query?.get('lat') && !locationDetails?.id) {
    noIndexNoFollow = true;
  }

  useMount(() => {
    if (!hideHeader && searchCriteria !== "COMPANY") {
      logPageView({ url: window.location.pathname + window.location.search });
      let layerUpdates: SearchPageView = {
        searchType,
        categoryCode,
        criteria,
        place: ''
      };

      if (locationDetails) layerUpdates.place = locationDetails.name;
      if (searchPortfolio) layerUpdates.portfolio_id = searchPortfolio.id;
      logSearchPageView(layerUpdates);
    }

  });

  let title = "";
  const neatStr = TEXT[`${searchType}_${categoryCode}_LABEL_UPPER_PLURAL${searchType === 'SCHEME' ? '_TITLE' : ''}`];
  const availabilityWording = forSale ? 'for sale' : 'for rent';

  if (!hideHeader && searchCriteria !== "COMPANY" && searchCriteria !== "PORTFOLIO") {
    let locationNameText = locationDetails?.uri && locationDetails?.name.toUpperCase() !== locationDetails?.uri.toUpperCase() && locationDetails.parent_name ? `${locationDetails?.name} - ${locationDetails.parent_name}` : locationDetails.name
    if (googleSearch) {
      title = searchType === 'SCHEME' ? `${neatStr}: ${locationDetails.name ? locationNameText : location}` : `${statistics ? statistics.total : ''} Retail units and shops ${availabilityWording} in ${locationNameText}`
    } else {
      title = searchType === 'SCHEME' ? `${neatStr} in ${locationDetails.name ? locationNameText : location}` : `${statistics ? statistics.total : ''} Retail units and shops ${availabilityWording} in ${locationNameText}`
    }
  } else {
    title = meta?.strapline;
  }
  const initialPath = usePathname();
  const combinedConfig = useNextRouting(config, initialPath, searchParams);
  const mapView = showMap;
  const listView = !mapView;
  const current = query?.get('current') ? Number(query.get('current')?.replace(/\D+/g, '') ?? 1) : 1;
  const [currentPage, setCurrentPage] = useState(current);


  const onRender = (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
  }
  return (
    <Profiler id="searchResults" onRender={onRender}>

      <div className={styles.resultWrapper}>
        <OurSearchProvider config={combinedConfig} initialData={initialDataWithFilters} >
          <WithSearch mapContextToProps={(context) => ({ ...context, defaultSortOrder: defaultSortOrder })}>
            {(props) => {
              if (!props.results) return null;
              const sizeTypes = [{ name: 'sq ft', value: 'size_ft_max', key: 'size_ft' }, { name: 'sq m', value: 'size_m_max', key: 'size_m' }];
              let defaultSizeType = { name: 'sq ft', value: 'size_ft_max', key: 'size_ft' };
              if (props.filters.length > 0) {
                const result = props.filters.filter((item) => item?.field?.startsWith('size_'));
                if (result.length > 0) {
                  defaultSizeType = result[0].field === 'size_ft_max' ? sizeTypes[0] : sizeTypes[1];
                }
              }

              const [sizeMeasurementType, setSizeMeasurementType] = useState(defaultSizeType);

              return (<>
                {!hideHeader && searchCriteria !== 'COMPANY' && searchCriteria !== 'PORTFOLIO' && (
                  <div className={styles.strapLine}>
                    <h1 className={styles.strapLineH1}>{title}</h1>
                  </div>
                )}
                {searchPortfolio?.id && <SponsoredBanner id={searchPortfolio.id} resultId={searchPortfolio.id} name={title} url={`/portfolio/${searchPortfolio.uri}`} dark={searchPortfolio?.data?.dark} logo_uri= { searchPortfolio.active && searchPortfolio.advertise && searchPortfolio.sponsored  ? `/images/portfolios/${searchPortfolio.uri.toLowerCase()}/logo.svg` : `${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${searchPortfolio.logo_uri}`} /> }
                
                <div className={`${styles.innerResultWrapper} ${mapView ? styles.mapView : ''} ${searchCriteria !== 'COMPANY' ? '' : styles.noSidebar}`} >

                  <Toolbar
                    location={locationDetails?.name}
                    hideLocationSearch={hideLocationSearch}
                    defaultSizeFt={statistics?.size_max}
                    defaultRent={statistics?.rent_max}
                    defaultSizeM={statistics?.size_max_m}
                    isPortfolioPage={searchPortfolio !== null}
                    type={searchType}
                    searchCriteria={searchCriteria}
                    sizeMeasurementType={sizeMeasurementType}
                    setSizeMeasurementType={setSizeMeasurementType}
                    sizeTypes={sizeTypes}
                    forSale={forSale}
                    defaultPrice={statistics?.price_max}
                    category_code={categoryCode}
                    showAdvancedFilters={showAdvancedFilters}
                    onShowAdvancedFilters={setShowAdvancedFilters}
                  />

                  <FilterPills mapView={mapView}
                    categories={initialDataWithFilters?.facets?.category_code?.[0].data}
                    forSale={forSale}
                    category_code={categoryCode}
                    defaultFilters={defaultFilters} />

                  <PaginationBar locationDetails={locationDetails} currentPage={currentPage} setCurrentPage={setCurrentPage} onShowMap={setShowMap}
                    showMap={showMap} {...props} />

                  {mapView && (
                    <Map lat={lat} lng={lng} results={props.results} portfolio={searchPortfolio} searchType={searchType} filters={props.filters} sizeMeasurementType={sizeMeasurementType} locationDetails={locationDetails} />
                  )}

                  {!props.isLoading && (
                    <>
                      {mapView && props.results.filter((r) => r.docType === 'scheme' || r.docType === 'property').length === 0 && (
                        <NoResults filters={props.filters} {...props} classType='mapCards extraPad' />
                      )}
                      {mapView && (
                        <MapCards searchType={searchType} results={props.results} filters={props.filters} portfolio={searchPortfolio} sizeMeasurementType={sizeMeasurementType} />
                      )}
                    </>
                  )}

                  {listView && (<ListView dxStats={dxStats} location={locationDetails?.name ? locationDetails.name : location} currentPage={currentPage} setCurrentPage={setCurrentPage} news={news} bids={bids} portfolio={searchPortfolio} {...props} filters={props.filters} loading={props.isLoading} locationDetails={locationDetails} statistics={statistics} recommendations={recommendations} destination={destination}
                    searchType={searchType} totalOtherLocations={recommendations?.length} categoryCode={categoryCode} searchCriteria={searchCriteria} sizeMeasurementType={sizeMeasurementType} hideStatistics={hideStatistics} forSale={forSale} />)}

                  {
                    props.isLoading && (
                      <div className={listView ? styles.resultMap : styles.mapCards}>
                        <Loading />
                      </div>
                    )
                  }

                </div>
                {!hideStatistics && searchCriteria !== 'COMPANY' && listView && (
                  <SearchData statistics={statistics} recommendations={recommendations} destination={destination} searchType={searchType}
                    categoryCode={categoryCode} locationDetails={locationDetails} searchCriteria={searchCriteria} portfolio={portfolio} news={news} forSale={forSale}
                  />
                )}
              </>
              );
            }}
          </WithSearch>
        </OurSearchProvider>
      </div >
    </Profiler>
  )
};

// Wrap the component so that we have proper acces to the initialised state
const SearchWithReducer = (props: PageProps) => {
  return (
    <Search {...props} />
  );
};

export default SearchWithReducer;
